import React, { useState, useEffect } from "react";

const DropdownWithCheckboxes = ({
  dropdownData = [],
  checkboxData = [],
  dropdownLabel = "Select Option",
  dropdownValueKey,
  dropdownLabelKey,
  checkboxValueKey,
  checkboxLabelKey,
  onDropdownChange,
  onCheckboxChange,
  loadingDropdown = false,
  loadingCheckboxes = false,
  dropdownError = null,
  checkboxError = null,
  selectedFormId = null,
  selectedPermissions = [],
}) => {
  // ✅ Initialize from props so edit mode works on first render
  const [selectedOption, setSelectedOption] = useState(
    selectedFormId != null ? selectedFormId : "",
  );
  const [selectedCheckboxes, setSelectedCheckboxes] = useState(
    Array.isArray(selectedPermissions) ? [...selectedPermissions] : [],
  );

  // ✅ Sync dropdown when parent changes it
  useEffect(() => {
    setSelectedOption(selectedFormId != null ? selectedFormId : "");
  }, [selectedFormId]);

  // ✅ Sync checkboxes only when parent explicitly changes them (edit prefill)
  useEffect(() => {
    const incoming = Array.isArray(selectedPermissions)
      ? selectedPermissions
      : [];

    setSelectedCheckboxes((prev) => {
      const isSame =
        prev.length === incoming.length &&
        prev.every((v, i) => v === incoming[i]);

      return isSame ? prev : [...incoming];
    });
  }, [selectedPermissions]);

  const handleDropdownChange = (e) => {
    const raw = e.target.value;
    const value = raw ? Number(raw) : "";
    setSelectedOption(value);
    setSelectedCheckboxes([]);
    if (onDropdownChange) onDropdownChange(value || null);
  };

  const handleCheckboxChange = (value) => {
    setSelectedCheckboxes((prev) => {
      const updated = prev.includes(value)
        ? prev.filter((v) => v !== value)
        : [...prev, value];
      if (onCheckboxChange) onCheckboxChange(updated);
      return updated;
    });
  };

  const handleSelectAll = () => {
    const allValues = checkboxData.map((item) => item[checkboxValueKey]);
    const updated =
      selectedCheckboxes.length === checkboxData.length ? [] : allValues;
    setSelectedCheckboxes(updated);
    if (onCheckboxChange) onCheckboxChange(updated);
  };

  const handleReset = () => {
    setSelectedOption("");
    setSelectedCheckboxes([]);
    if (onDropdownChange) onDropdownChange(null);
    if (onCheckboxChange) onCheckboxChange([]);
  };

  return (
    <div className="dwc-container">
      <div className="dwc-dropdown-section">
        <label className="dwc-label" htmlFor={`dwc-${dropdownLabel}`}>
          {dropdownLabel}
        </label>

        {loadingDropdown ? (
          <div className="dwc-loader">
            <span className="dwc-spinner"></span> Loading...
          </div>
        ) : dropdownError ? (
          <p className="dwc-error">⚠️ {dropdownError}</p>
        ) : (
          <select
            id={`dwc-${dropdownLabel}`}
            className="dwc-select"
            value={selectedOption}
            onChange={handleDropdownChange}
          >
            <option value="">-- Select --</option>
            {dropdownData.map((item, index) => (
              <option
                key={item[dropdownValueKey] ?? index}
                value={item[dropdownValueKey]}
              >
                {item[dropdownLabelKey]}
              </option>
            ))}
          </select>
        )}
      </div>

      {selectedOption !== "" && selectedOption != null && (
        <div className="dwc-checkbox-section">
          <div className="dwc-checkbox-header">
            <h3 className="dwc-checkbox-title">
              Options for <span>{selectedOption}</span>
            </h3>
            {!loadingCheckboxes && checkboxData.length > 0 && (
              <div className="dwc-header-actions">
                <span className="dwc-selected-count">
                  {selectedCheckboxes.length} / {checkboxData.length} selected
                </span>
                <button
                  type="button"
                  className="dwc-select-all-btn"
                  onClick={handleSelectAll}
                >
                  {selectedCheckboxes.length === checkboxData.length
                    ? "Deselect All"
                    : "Select All"}
                </button>
              </div>
            )}
          </div>

          {loadingCheckboxes ? (
            <div className="dwc-loader">
              <span className="dwc-spinner"></span> Loading...
            </div>
          ) : checkboxError ? (
            <p className="dwc-error">⚠️ {checkboxError}</p>
          ) : checkboxData.length === 0 ? (
            <p className="dwc-empty">No options available.</p>
          ) : (
            <div className="dwc-checkbox-grid">
              {checkboxData.map((item, index) => {
                const val = item[checkboxValueKey];
                const checked = selectedCheckboxes.includes(val);
                return (
                  <label
                    key={val ?? index}
                    className={`dwc-checkbox-item ${checked ? "dwc-checked" : ""}`}
                  >
                    <input
                      type="checkbox"
                      className="dwc-checkbox"
                      checked={checked}
                      onChange={() => handleCheckboxChange(val)}
                    />
                    <span className="dwc-checkbox-label">
                      {item[checkboxLabelKey]}
                    </span>
                  </label>
                );
              })}
            </div>
          )}
        </div>
      )}

      {selectedCheckboxes.length > 0 && (
        <div className="dwc-summary">
          <h4 className="dwc-summary-title">
            Selected ({selectedCheckboxes.length})
          </h4>
          <ul className="dwc-summary-list">
            {checkboxData
              .filter((item) =>
                selectedCheckboxes.includes(item[checkboxValueKey]),
              )
              .map((item, index) => (
                <li
                  key={item[checkboxValueKey] ?? index}
                  className="dwc-summary-item"
                >
                  {item[checkboxLabelKey]}
                </li>
              ))}
          </ul>
        </div>
      )}

      {(selectedOption || selectedCheckboxes.length > 0) && (
        <button type="button" className="dwc-reset-btn" onClick={handleReset}>
          Reset
        </button>
      )}
    </div>
  );
};

export default DropdownWithCheckboxes;
